import { SingleSelectDropdown, UniversityDropdown } from "@components/library";
import KeywordsDropdown from "@components/library/Dropdowns/KeywordsDropdown/KeywordsDropdown";
import { COUNTRY_OPTIONS } from "@constants";
import { getOptionFromValue, getValueFromOption } from "@utils/dropdownUtils";
import { Dispatch, SetStateAction } from "react";
import { SCIENTIST_ROLES } from "src/constants";
import api from "src/requests/request";
import { BaseOnboardingProfileInfo } from "../OnboardingContainer";
import * as S from "../StepStyles";

export type UniversityScientistOnboardingProfileInfo = BaseOnboardingProfileInfo &
  Partial<{
    universityHasCountry: boolean;
    country_code: string | undefined;
    role: string | undefined;
    disciplines: string[];
    areasOfExpertise: string[];
  }>;

interface Props {
  profileInfo: UniversityScientistOnboardingProfileInfo;
  setProfileInfo: Dispatch<SetStateAction<UniversityScientistOnboardingProfileInfo>>;
  prefillInstitutionName?: string;
  isPrefillReadonly?: boolean;
}

export const SCIENTIST_ROLE_OPTIONS = SCIENTIST_ROLES.map(getOptionFromValue);

function ResearchInstitutionScientist({
  profileInfo,
  setProfileInfo,
  prefillInstitutionName = "",
  isPrefillReadonly = true,
}: Props) {
  const { university, universityHasCountry, role, disciplines, areasOfExpertise, country_code } =
    profileInfo;

  const handleSelectUniversityOption = async (option) => {
    if (!option) return;

    const universityName = option.label;

    const responseJson = await api.get("/universities/has_country", {
      params: { name: universityName },
    });
    const { has_country } = responseJson.data;

    setProfileInfo((oldProfileInfo) => ({
      ...oldProfileInfo,
      university: universityName,
      universityHasCountry: has_country,
    }));
  };

  return (
    <>
      <S.DropdownRow>
        <S.DropdownTitle>Research institution</S.DropdownTitle>
        {isPrefillReadonly && prefillInstitutionName.length > 0 ? (
          <S.Input type="text" value={prefillInstitutionName} tabIndex={-1} fixed readOnly />
        ) : (
          <>
            <UniversityDropdown
              value={getOptionFromValue(university)}
              onChange={handleSelectUniversityOption}
              defaultInputValue={prefillInstitutionName}
              data-testid="ri-scientist-institution-name-input"
            />
            <S.ContactLink>
              You can{" "}
              <a
                href="/contact?message=Hello, I can't find my institution as an option. My institution is: "
                target="_blank"
              >
                request that we add your institution
              </a>{" "}
              if you don&rsquo;t see it above.
            </S.ContactLink>
          </>
        )}
      </S.DropdownRow>
      {!universityHasCountry && (!isPrefillReadonly || prefillInstitutionName.length === 0) && (
        <S.DropdownRow>
          <S.DropdownTitle>Institution location</S.DropdownTitle>
          <SingleSelectDropdown
            withSearchIcon
            placeholder="Search countries"
            value={getOptionFromValue(country_code)}
            options={COUNTRY_OPTIONS}
            onChange={(option) =>
              setProfileInfo((oldProfileInfo) => ({
                ...oldProfileInfo,
                country_code: getValueFromOption(option),
              }))
            }
            data-testid="ri-scientist-institution-location-input"
          />
        </S.DropdownRow>
      )}
      <S.DropdownRow>
        <S.DropdownTitle>Which of the following best describes your role?</S.DropdownTitle>
        <SingleSelectDropdown
          placeholder="Select your role"
          options={SCIENTIST_ROLE_OPTIONS}
          onChange={(e) =>
            setProfileInfo((oldProfileInfo) => ({
              ...oldProfileInfo,
              role: getValueFromOption(e),
            }))
          }
          value={getOptionFromValue(role)}
          data-testid="ri-scientist-role-input"
        />
      </S.DropdownRow>
      <S.DropdownRow>
        <S.DropdownTitle>Which disciplines are most relevant to your work?</S.DropdownTitle>
        <KeywordsDropdown
          keywordType="discipline"
          targetType="scientist"
          value={disciplines ?? []}
          onChange={(selection) => setProfileInfo({ ...profileInfo, disciplines: selection })}
        />
      </S.DropdownRow>
      <S.DropdownRow>
        <S.DropdownTitle>What is your expertise?</S.DropdownTitle>
        <S.DropdownSubtitle>Choose between 3 and 10 terms.</S.DropdownSubtitle>
        <KeywordsDropdown
          keywordType="area_of_expertise"
          targetType="scientist"
          value={areasOfExpertise ?? []}
          onChange={(selection) => setProfileInfo({ ...profileInfo, areasOfExpertise: selection })}
        />
      </S.DropdownRow>
    </>
  );
}

export default ResearchInstitutionScientist;
